/**
 * Status items of the preview package (W2-02), rendered in the shell's banner row:
 *  - "Preview differs from export" (the only implementation of that banner): shown while hidden lines, solo lines,
 *    muted layers or dimming change what the preview draws, with what is active and "Show everything";
 *  - the full-window host of Theater and test play: nothing in the row itself, a portal over the whole window while
 *    either is active (from any workspace, also when no preview panel is mounted).
 */

import { createPortal } from 'react-dom';
import type { AppServices } from '../../app/registry/types';
import { useT } from '../../i18n/useT';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import { filterSummary, previewDiffers } from './filters';
import { runCommand, useEditorValue, useProjectOpen, useTestPlay } from './hooks';
import { m } from './preview.messages';
import { TestPlay } from './TestPlay';
import { Theater } from './Theater';
import s from './Preview.module.css';

export function PreviewDiffersBanner(props: { readonly services: AppServices }) {
  const { services } = props;
  const t = useT();
  const open = useProjectOpen(services);
  const filters = useEditorValue(services, (st) => st.filters);
  if (!open || !previewDiffers(filters)) return null;
  const sum = filterSummary(filters);
  const parts: string[] = [];
  if (sum.hidden > 0) parts.push(t(m.bannerHidden, { count: sum.hidden }));
  if (sum.solo > 0) parts.push(t(m.bannerSolo, { count: sum.solo }));
  if (sum.muted > 0) parts.push(t(m.bannerMuted, { count: sum.muted }));
  if (sum.dim) parts.push(t(m.bannerDim));
  const list = parts.reduce((a, b) => t(m.listJoin, { a, b }));
  return (
    <div className={s.banner} role="status" data-banner="preview.differs">
      <Icon name="EyeOff" size={16} />
      <span className={s.bannerText}>{t(m.bannerDiffers, { parts: list })}</span>
      <Button
        size="sm"
        variant="secondary"
        icon="Eye"
        command="preview.clearFilters"
        onClick={() => {
          runCommand(services, 'preview.clearFilters');
        }}
      >
        {t(m.bannerShowAll)}
      </Button>
    </div>
  );
}

export function PreviewStageHost(props: { readonly services: AppServices }) {
  const { services } = props;
  const open = useProjectOpen(services);
  const theater = useEditorValue(services, (st) => st.preview.theater);
  const testPlay = useTestPlay(services);
  if (!open || typeof document === 'undefined') return null;
  if (testPlay.active) return createPortal(<TestPlay services={services} />, document.body);
  if (theater) return createPortal(<Theater services={services} />, document.body);
  return null;
}
